import { useCache } from "@hyper-fetch/react";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import * as CookieConsent from "vanilla-cookieconsent";

import { pageViewFromMatches } from "./page-view";
import { identifyPosthogUser, syncPosthogConsent, trackPageView } from "./posthog";
import { sdk } from "@/api/sdk";
import { COOKIE_CATEGORIES } from "@/features/cookies/modules/cookie-consent/config/cookie-consent.config";

const readAnalyticsConsent = () => CookieConsent.validConsent() && CookieConsent.acceptedCategory(COOKIE_CATEGORIES.analytics);

/**
 * Headless mount that keeps PostHog in step with the cookie banner and the signed-in person.
 * vanilla-cookieconsent fires `cc:onConsent` on every load with a stored answer and `cc:onChange`
 * when someone changes it, so PostHog starts only once the visitor has decided. A page view goes
 * out when the router resolves a page, after its loaders, so it carries the page's breadcrumb name.
 */
export function PosthogProvider() {
  const [decided, setDecided] = useState(false);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);
  // Reads the cache only: whoever needs the user fetches it, this never adds a request.
  const { data: me } = useCache(sdk.users.me.$get);
  const userId = me?.user.id;
  const email = me?.user.email;
  const name = [me?.user.firstName, me?.user.lastName].filter(Boolean).join(" ") || undefined;
  const router = useRouter();

  useEffect(() => {
    // The page server-rendered before this mounted has already resolved.
    trackPageView(pageViewFromMatches(router.state.matches));
    return router.subscribe("onResolved", () => trackPageView(pageViewFromMatches(router.state.matches)));
  }, [router]);

  useEffect(() => {
    const onConsent = () => {
      setDecided(true);
      setAnalyticsAllowed(readAnalyticsConsent());
    };
    window.addEventListener("cc:onConsent", onConsent);
    window.addEventListener("cc:onChange", onConsent);
    return () => {
      window.removeEventListener("cc:onConsent", onConsent);
      window.removeEventListener("cc:onChange", onConsent);
    };
  }, []);

  useEffect(() => {
    if (!decided) return;
    void syncPosthogConsent(analyticsAllowed);
    if (userId && email) identifyPosthogUser(userId, { email, name });
  }, [decided, analyticsAllowed, userId, email, name]);

  return null;
}
